import { Tabs, TabsList, TabsTrigger } from '@miq/design-system/components/ui/tabs';

import { monthLabel } from '../data/format';
import type { Period } from '../data/reads';

/**
 * Which month this screen is answering for.
 *
 * The screen used to say "This month" and mean it: on the 1st, a customer
 * whose usage was last month met a page of zeros with no way to look back at
 * the figures they were about to be invoiced for — while Billing, one click
 * away, carried a previous-period tile for exactly that reason. The two
 * screens disagreed about which month mattered, worst on the days somebody is
 * most likely to check.
 *
 * The current month is the LAST entry and the default, matching the chart's
 * axis, which draws oldest-first for the same reason. It is selected as
 * `null` rather than as its own date: "did not ask" is what the service reads
 * as current, so a session left open across midnight on the 1st follows the
 * service rather than pinning the month it was opened in.
 */
export function MonthPicker({
  months,
  selected,
  onSelect,
  period,
}: {
  months: string[];
  selected: string | null;
  onSelect: (month: string | null) => void;
  /** The window the figures below were actually cut on. Passed as the period
   * itself rather than as a formatted string, so the caption cannot drift
   * from the arithmetic it describes — and so two screens sharing this
   * control cannot word the same window two ways. */
  period: Period;
}) {
  const current = months[months.length - 1];
  const active = selected ?? current;

  return (
    <div className="shrink-0 text-right">
      <Tabs
        value={active}
        onValueChange={(month) =>
          onSelect(month === current ? null : month)
        }
      >
        <TabsList>
          {months.map((month) => (
            <TabsTrigger key={month} value={month}>
              {monthLabel(`${month}-01T00:00:00Z`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="mt-1 text-xs text-muted-foreground">
        {periodLabel(period)}
      </div>
    </div>
  );
}


/** The window a period covers, as a reader would write it down. */
function periodLabel(period: Period): string {
  const from = new Date(period.start);
  // The window's end is exclusive — the first instant of the next month — so
  // the last day it covers is the one before it.
  const to = new Date(new Date(period.end).getTime() - 1);
  const day = { month: 'short', day: 'numeric', timeZone: 'UTC' } as const;

  return `${from.toLocaleDateString(undefined, day)} – ${to.toLocaleDateString(undefined, {
    ...day,
    year: 'numeric',
  })}`;
}
